/*
 * @Author: mina.min
 * @Date: 2025-08-14 18:30:00
 * @LastEditors: mina.min
 * @LastEditTime: 2025-08-25 17:04:29
 * @FilePath: /OTA-frontend/src/pages/ModuleManagement/ProjectPhase.tsx
 * @Description: 项目阶段管理组件
 */
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Card, Form, Input, message, Modal, Space, Table, Typography } from 'antd'
import React, { useEffect, useState } from 'react'
import {
  deleteProjectPhase,
  editProjectPhase,
  getProjectPhaseDetail,
  getProjectPhaseList,
  ProjectPhaseItem,
  saveProjectPhase,
} from '../../services/projectPhase'
import { formatToCST } from '../../utils'
import './ProjectPhase.scss'

const { Title } = Typography

const ProjectPhase: React.FC = () => {
  const [form] = Form.useForm()
  const [modalVisible, setModalVisible] = useState(false)
  const [editingPhase, setEditingPhase] = useState<ProjectPhaseItem | null>(null)
  const [loading, setLoading] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [phaseList, setPhaseList] = useState<ProjectPhaseItem[]>([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })

  useEffect(() => {
    fetchProjectPhaseList()
  }, [pagination.current, pagination.pageSize])

  const fetchProjectPhaseList = async () => {
    try {
      setLoading(true)
      const response = await getProjectPhaseList(pagination.current, pagination.pageSize)
      if (response.code === '200') {
        setPhaseList(response.data.list)
        setPagination(prev => ({
          ...prev,
          total: response.data.total,
        }))
      } else {
        message.error(response.info || '获取项目阶段列表失败')
      }
    } catch (error: any) {
      message.error(error?.message || '获取项目阶段列表失败')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingPhase(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = async (record: ProjectPhaseItem) => {
    try {
      setEditLoading(true)
      // 调用详情接口获取完整数据
      const response = await getProjectPhaseDetail(record.phaseId)
      if (response.code === '200') {
        setEditingPhase(response.data)
        form.setFieldsValue(response.data)
        setModalVisible(true)
      } else {
        message.error(response.info || '获取项目阶段详情失败')
      }
    } catch (error: any) {
      message.error(error?.message || '获取项目阶段详情失败')
    } finally {
      setEditLoading(false)
    }
  }

  const handleDelete = async (phaseId: string, phaseName: string) => {
    // 显示确认弹窗
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除项目阶段"${phaseName}"吗？此操作不可恢复。`,
      okText: '确定删除',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true)
          // 调用删除接口
          const response = await deleteProjectPhase({ phaseId })

          if (response.code === '200') {
            message.success('删除成功')
            // 刷新列表
            fetchProjectPhaseList()
          } else {
            message.error(response.info || '删除失败')
          }
        } catch (error: any) {
          message.error(error?.message || '删除失败')
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      if (editingPhase) {
        // 编辑模式 - 调用编辑接口
        const response = await editProjectPhase({
          phaseId: editingPhase.phaseId,
          phaseName: editingPhase.phaseName, // 使用原始的阶段名称，不修改
          remark: values.remark,
        })

        if (response.code === '200') {
          message.success('编辑成功')
          // 刷新列表
          fetchProjectPhaseList()
        } else {
          message.error(response.info || '编辑失败')
        }
      } else {
        // 新增模式 - 调用保存接口
        const response = await saveProjectPhase({
          phaseName: values.phaseName,
          remark: values.remark,
        })

        if (response.code === '200') {
          message.success('新增成功')
          // 刷新列表
          fetchProjectPhaseList()
        } else {
          message.error(response.info || '新增失败')
        }
      }

      setModalVisible(false)
      form.resetFields()
    } catch (error: any) {
      message.error(error?.message || '操作失败')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '阶段ID',
      dataIndex: 'phaseId',
      key: 'phaseId',
      width: 180,
    },
    {
      title: '阶段名称',
      dataIndex: 'phaseName',
      key: 'phaseName',
      width: 150,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (createTime: string) => formatToCST(createTime),
    },
    {
      title: '最后更新时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 160,
      render: (updateTime: string) => formatToCST(updateTime),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: ProjectPhaseItem) => (
        <Space size='middle'>
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            loading={editLoading}
          >
            编辑
          </Button>
          <Button
            type='link'
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.phaseId, record.phaseName)}
            loading={loading}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className='project-phase'>
      <div className='project-phase-header'>
        <Title level={4}>项目阶段管理</Title>
        <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
          添加阶段
        </Button>
      </div>

      <Card className='project-phase-card'>
        <Table
          columns={columns}
          dataSource={phaseList}
          rowKey='phaseId'
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || prev.pageSize,
              }))
            },
          }}
        />
      </Card>

      <Modal
        title={editingPhase ? '编辑项目阶段' : '添加项目阶段'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='phaseName'
            label='阶段名称'
            rules={[{ required: true, message: '请输入阶段名称' }]}
          >
            <Input
              placeholder='请输入阶段名称'
              disabled={!!editingPhase} // 编辑模式下禁用
            />
          </Form.Item>

          <Form.Item name='remark' label='备注'>
            <Input.TextArea placeholder='请输入备注（可选）' rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default ProjectPhase
